// DashTagPopup.tsx — ein Tag des Wochen-Boards als Popup (T-394, 26.10.2026:
// „Wenn ich auf einen Tag in der Woche drücke, sollte sich dieser öffnen",
// PO: „als Popup"). Auf dem iPhone waren im Board nur abgeschnittene Titel zu
// sehen; hier stehen alle Termine des Tages mit Uhrzeit, vollem Titel, Ort und
// Teams-Hinweis, darunter die Wiedervorlagen. Ein Tipp auf einen Termin öffnet
// die bestehende Vorschau (`DashTerminPopover`), „Im Kalender öffnen" führt auf
// den Tag im Kalender.
//
// Portal an document.body wie `DashTerminPopover` — sonst schnitte das
// `overflow: hidden` der DashCard das Popup ab. Desktop: mittig mit Abdunklung;
// Handy: als Bottom-Sheet am unteren Rand. Rein inline über REFINED-Tokens
// (keine `.kvp-rd`-Klasse → keine Portal-min-height-Falle #648). Die Vorschau
// liegt mit z-index 4000 darüber.

import * as React from "react";
import { useEffect, useRef } from "react";
import { Dismiss16Regular, Open16Regular } from "@fluentui/react-icons";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";
import type { SourcedCalendarEvent } from "../../services/calendarService";
import type { TwvItemDto } from "../../services/vorgangService";
import { REFINED } from "../risikoanalyseStep/tokens";
import { deTime } from "./DashboardShared";
import { Row } from "./TerminRow";
import { tagUeberschrift } from "./termineTag";

const GOLD = "#e6a417";

/** Der geöffnete Tag — dieselben Daten wie seine Board-Spalte. */
export interface DashTag {
    date: Date;
    /** Lokaler Tag als „YYYY-MM-DD" — das `date` des Kalender-Links. */
    key: string;
    isToday: boolean;
    events: SourcedCalendarEvent[];
    twv: TwvItemDto[];
}

/** Ort und Teams-Hinweis einer Terminzeile. */
const ortUndTeams = (ev: SourcedCalendarEvent): string =>
    [ev.location?.trim() ?? "", ev.isOnlineMeeting ? "Teams-Besprechung" : ""].filter(Boolean).join(" · ");

const abschnittStyle: React.CSSProperties = {
    fontSize: "0.72rem",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: REFINED.ink3,
    padding: "14px 6px 2px",
};

export const DashTagPopup: React.FC<{
    tag: DashTag | null;
    isPhone: boolean;
    /** Ist die Vorschau eines Termins offen? Dann schließt Escape zuerst nur sie. */
    vorschauOffen: boolean;
    onClose: () => void;
    onEventClick: (ev: SourcedCalendarEvent, e: React.MouseEvent) => void;
    onEventDoubleClick: (ev: SourcedCalendarEvent) => void;
    onTwv: (t: TwvItemDto) => void;
    onOpenCalendar: (key: string) => void;
}> = ({ tag, isPhone, vorschauOffen, onClose, onEventClick, onEventDoubleClick, onTwv, onOpenCalendar }) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const vorschauRef = useRef(vorschauOffen);
    vorschauRef.current = vorschauOffen;
    /**
     * War beim Drücken auf die Abdunklung eine Vorschau offen? Dann schließt
     * der Klick nur sie (das macht `DashTerminPopover` selbst), nicht den Tag.
     * Gemerkt beim pointerdown, weil die Vorschau bis zum click schon zu ist.
     */
    const vorschauBeimDruckRef = useRef(false);

    // Escape schließt — außer eine Vorschau liegt darüber: die schließt sich
    // selbst, das Popup bleibt.
    useEffect(() => {
        if (!tag) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !vorschauRef.current) onClose();
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [tag, onClose]);

    // Fokus ins Popup, damit Tastatur und Vorlesewerkzeug dort weitermachen.
    useEffect(() => {
        if (tag) panelRef.current?.focus();
    }, [tag]);

    if (!tag) return null;
    const titel = tagUeberschrift(tag.date);
    const leer = tag.events.length === 0 && tag.twv.length === 0;

    return createPortal(
        <div
            data-tag-popup-scrim
            onPointerDown={() => {
                vorschauBeimDruckRef.current = vorschauRef.current;
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !vorschauBeimDruckRef.current) onClose();
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 3500,
                backgroundColor: "rgba(36, 21, 34, 0.32)",
                display: "flex",
                alignItems: isPhone ? "flex-end" : "center",
                justifyContent: "center",
                padding: isPhone ? 0 : 16,
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={`Termine am ${titel}`}
                tabIndex={-1}
                style={{
                    width: isPhone ? "100%" : 460,
                    maxWidth: isPhone ? "100%" : "calc(100vw - 32px)",
                    maxHeight: isPhone ? "85vh" : "80vh",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: REFINED.white,
                    border: `1px solid ${REFINED.line}`,
                    borderRadius: isPhone ? "18px 18px 0 0" : 16,
                    boxShadow: "0 16px 40px rgba(58,12,49,0.22)",
                    fontFamily: REFINED.fontUI,
                    outline: "none",
                    paddingBottom: isPhone ? "env(safe-area-inset-bottom)" : 0,
                }}
            >
                {/* Kopf: Datum, „Heute", Schließen */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "16px 16px 10px",
                        borderBottom: `1px solid ${REFINED.line}`,
                    }}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {tag.isToday && (
                            <div style={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: GOLD }}>
                                Heute
                            </div>
                        )}
                        <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: REFINED.ink, lineHeight: 1.25 }}>
                            {titel}
                        </h3>
                    </div>
                    <button
                        type="button"
                        aria-label="Schließen"
                        onClick={onClose}
                        style={{
                            flexShrink: 0,
                            width: isPhone ? 44 : 32,
                            height: isPhone ? 44 : 32,
                            margin: isPhone ? "-8px -8px -8px 0" : 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 999,
                            border: "none",
                            background: "transparent",
                            color: REFINED.ink3,
                            cursor: "pointer",
                            padding: 0,
                        }}
                    >
                        <Dismiss16Regular />
                    </button>
                </div>

                {/* Inhalt: Termine, dann Wiedervorlagen */}
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 10px 6px" }}>
                    {leer ? (
                        <div style={{ padding: "24px 6px", textAlign: "center", color: REFINED.ink3, fontSize: "0.9rem" }}>
                            Keine Termine oder Wiedervorlagen an diesem Tag.
                        </div>
                    ) : (
                        <>
                            {tag.events.length > 0 && <div style={abschnittStyle}>Termine</div>}
                            {tag.events.map((e, idx) => (
                                <Row
                                    key={`tp-${e.source.address}-${e.id}`}
                                    border={idx > 0}
                                    time={e.isAllDay ? "" : `${deTime(e.start.dateTime)}`}
                                    allDay={e.isAllDay || false}
                                    dot={e.source.color || REFINED.aub}
                                    title={e.subject || "(ohne Titel)"}
                                    sub={ortUndTeams(e)}
                                    chip={{ kind: "termin", online: e.isOnlineMeeting }}
                                    onClick={(me) => onEventClick(e, me)}
                                    onDoubleClick={() => onEventDoubleClick(e)}
                                    voll
                                />
                            ))}
                            {tag.twv.length > 0 && <div style={abschnittStyle}>Wiedervorlagen</div>}
                            {tag.twv.map((t, idx) => (
                                <Row
                                    key={`tpw-${t.kind}-${t.id}`}
                                    border={idx > 0}
                                    time="—"
                                    dot={GOLD}
                                    title={t.titel}
                                    sub=""
                                    chip={{ kind: "twv" }}
                                    onClick={() => onTwv(t)}
                                    voll
                                />
                            ))}
                        </>
                    )}
                </div>

                {/* Fuß: auf den Tag im Kalender */}
                <div style={{ padding: "10px 16px 14px", borderTop: `1px solid ${REFINED.line}` }}>
                    <button
                        type="button"
                        onClick={() => onOpenCalendar(tag.key)}
                        style={{
                            width: "100%",
                            minHeight: isPhone ? 44 : 40,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            borderRadius: 12,
                            border: `1px solid ${REFINED.line}`,
                            background: REFINED.white,
                            color: REFINED.aub,
                            cursor: "pointer",
                            fontFamily: REFINED.fontUI,
                            fontSize: "0.88rem",
                            fontWeight: 800,
                        }}
                    >
                        <Open16Regular /> Im Kalender öffnen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default DashTagPopup;
